/**
 * Shared WebGPU plumbing for every three.js surface in this app — the
 * `/assets` model viewer and the `/fly` flight scene both go through here so
 * AD-005 ("WebGPURenderer is the sole render path, no WebGL fallback") is
 * enforced in exactly one place rather than re-argued per scene, and now
 * colour management too.
 *
 * **Colour management: OFF, app-wide.** This is a palette-indexed 1990 VGA
 * game — every drawn byte, from `BARON.PAL`'s vertex colours to the baked
 * sprite sheets to the flat sky/ground hexes, *is* the value the original put
 * in its framebuffer, not a photographic quantity to be lit and tone-mapped.
 * Left at three's default, the renderer treats those bytes as already being
 * in *linear* working space and sRGB-encodes them again on output, so every
 * baked model in `/fly` draws visibly brighter than its palette entry
 * (measured on a WebGPU readback: index 18 `166,130,52` reached the
 * framebuffer as `211,189,125` — see `ramp-material.ts`'s module doc, which
 * deliberately left this one decision for here). `ColorManagement.enabled =
 * false` (three.js, `math/ColorManagement.js`) short-circuits every one of
 * three's sRGB<->linear conversions in one place — vertex colours, texture
 * decode regardless of a texture's own `colorSpace` tag, hex/`THREE.Color`
 * literals, and the renderer's output encode — which turns the whole pipeline
 * into the raw byte pass-through the original's indexed framebuffer actually
 * was. It has to run before any texture or renderer exists, and every
 * three.js surface in this app already imports this module for
 * `disableWebGlFallback`, so the assignment lives here rather than needing
 * its own bootstrap file.
 *
 * `NoColorSpace` on the palette texture, the ramp material's deltas texture
 * and the sprite-sheet textures (`screen-sprites.ts`, `combat-visuals.ts`,
 * `model-scene-primitives.ts`) stays *explicit* even though it is now a no-op
 * — `ColorManagement.enabled === false` makes every `colorSpace` tag inert,
 * but the tag still documents which of these are true, unprocessed game bytes
 * so a future `enabled = true` cannot silently re-brighten only the untagged
 * ones while leaving this comment's claim unverifiable.
 */

import * as THREE from "three/webgpu";
import { rendererPin } from "./renderer-mode.ts";

THREE.ColorManagement.enabled = false;

/**
 * Whether this context has a WebGPU implementation at all.
 *
 * **No longer a gate on the flight routes** — `three/renderer-mode.ts` takes
 * this as one of four inputs and answers with a *backend* rather than with a
 * refusal, so `/fly`, `/duel` and `/vcr` come up on WebGL when it is `false`
 * instead of printing `WEBGPU_UNAVAILABLE_NOTE`. The `/assets` viewers still
 * gate on it: they are a review suite for baked data rather than the game, the
 * note is the right answer there, and giving each of them a fallback path would
 * be three more renderers to keep in visual parity for no player-facing gain.
 */
export function hasWebGpu(): boolean {
  return typeof navigator !== "undefined" && "gpu" in navigator && navigator.gpu != null;
}

/**
 * `WebGPURenderer`'s own constructor unconditionally wires up a
 * `_getFallback` that silently swaps in a WebGL2 backend if the WebGPU
 * backend's `init()` throws (three.js source, `WebGPURenderer.js`) — there
 * is no public constructor option to opt out (`forceWebGL` only forces the
 * *other* direction). Left alone, `renderer.init()` then *resolves*
 * successfully on the WebGL2 fallback instead of rejecting — confirmed
 * empirically in a headless Chromium context with no real WebGPU adapter
 * (console warning: "WebGPURenderer: WebGPU is not available, running
 * under WebGL2 backend."). Setting the undocumented field to `null` forces
 * `init()` to reject instead, which keeps this AD-005-compliant.
 * `WebGPURenderer` doesn't declare this field in its public type at all,
 * hence the loose cast — isolated here rather than inline at call sites.
 *
 * **This stays, and the automatic fallback is built on top of it rather than
 * by deleting it.** The half of AD-005 worth keeping is that a WebGL picture
 * must never arrive *silently*: three's own swap leaves `renderer` reporting
 * itself a `WebGPURenderer`, changes the depth attachment underneath the scene
 * (`docs/engineering/webgl-fallback.md` §3), and tells nobody. Nulling the
 * field turns that into a rejection, which is what lets
 * `three/renderer-open.ts` catch it, name it, persist it, print it on the
 * loading screen and on PREFERENCES, and rebuild the renderer deliberately.
 * An automatic fallback and an *unannounced* one are different features.
 */
interface RendererWithPrivateFallback {
  /** Undocumented three.js internal field — optional here purely so the assertion below type-checks without `any`/`unknown`. */
  _getFallback?: (() => unknown) | null;
}

export function disableWebGlFallback(renderer: THREE.WebGPURenderer): void {
  (renderer as THREE.WebGPURenderer & RendererWithPrivateFallback)._getFallback = null;
}

export interface DeviceLossInfo {
  readonly reason: string;
  readonly message: string;
}

/** The one sliver of the `GPUDevice` interface this module needs. */
interface WebGpuDeviceHandle {
  readonly lost: Promise<DeviceLossInfo>;
}

/**
 * `init()` resolving only means the backend object was constructed — it
 * doesn't guarantee the GPUDevice stays usable (observed: a headless
 * Chromium + software-Vulkan sandbox can report an adapter and "initialize"
 * successfully, then have the device fail asynchronously afterwards with no
 * synchronously-catchable error, leaving a silently blank canvas). Watching
 * `device.lost` turns that into a clear status instead of a canvas that
 * never draws.
 */
export function watchDeviceLoss(renderer: THREE.WebGPURenderer, onLost: (info: DeviceLossInfo) => void): void {
  const device = (renderer.backend as { device?: WebGpuDeviceHandle }).device;
  void device?.lost.then(onLost);
}

/**
 * `/fly?renderer=webgl` — the **capture hatch**, and the reason it exists is a
 * lesson rather than a feature.
 *
 * **The parsing moved to `three/renderer-mode.ts`** (`rendererPin`), which reads
 * the same key for both directions because `?renderer=webgpu` is now the escape
 * hatch out of a remembered driver failure. What stays here is the argument for
 * the hatch itself, because it is the argument every capture in this repo rests
 * on and it belongs next to `disableWebGlFallback` rather than next to a
 * `URLSearchParams` call.
 *
 * `docs/engineering/lessons.md` records three days spent on a 3D COCKPIT bug
 * that could not be looked at, and the rule it left: *"'we cannot screenshot X'
 * is a statement about a renderer, not about a feature."* On this project's own
 * hardware a WebGPU-composited page has no working readback at all —
 * `toDataURL`, `page.screenshot`, CDP `captureScreenshot`, `import -window root`
 * and `x11grab` each fail or lie, and a headless Chromium driven onto
 * SwiftShader loses the device outright ("GPU device lost (destroyed)"). Under
 * `WebGPURenderer`'s own WebGL2 backend the same scene, the same materials and
 * the same textures render into a canvas Playwright reads back in milliseconds.
 *
 * **This is not the AD-005 fallback.** AD-005 forbids *silently* degrading to
 * WebGL when WebGPU is missing, which is why `disableWebGlFallback` exists and
 * stays; a player who has no WebGP gets the note below and not a quietly
 * different picture. This is the opposite: an explicit pin, reachable only by
 * typing a query string, that nothing links to and nothing persists — the same
 * standing `effects-settings.ts` gives `?treatment=measured`.
 *
 * The one backend difference that could invalidate a capture was settled by
 * reading three's own source rather than assumed away: `GLSLNodeBuilder.isFlipY()`
 * is true and `WGSLNodeBuilder`'s is false, so an `ImageBitmap`'s UVs are flipped
 * in the shader under WebGL and not under WebGPU. It touches image *sources*,
 * which is the dash quad's texture and nothing else here, and the quad's own
 * window is pinned by `cockpit-dash-quad.test.ts` in both derivations.
 */
export function forcedWebGlCapture(search: string | undefined): boolean {
  return rendererPin(search) === "webgl";
}

/**
 * Shown by the `/assets` viewers when the browser/context has no WebGPU.
 *
 * **The flight routes no longer use it** — they fall back to WebGL and say so
 * (`three/renderer-mode.ts`). The viewers keep it for the reason `hasWebGpu`'s
 * doc gives: they are a data-review suite, not the game.
 */
export const WEBGPU_UNAVAILABLE_NOTE =
  "WebGPU is not available in this browser/context (navigator.gpu is undefined). The asset viewers target three.js WebGPURenderer only — the game itself falls back to WebGL (see PREFERENCES).";
